<!-- @format -->
<template>
    <div class="disclaimer page">
        <div class="container body-content">
            <h2 class="title">GlassHive<sup>TM</sup> Privacy Policy</h2>
            <p>Last Updated: February 11, 2025</p>
            <p>
                The website located at
                <a href="https://www.glasshive.com/" target="_blank">https://www.glasshive.com/</a>
                (“<strong>Site</strong>”) and the features, functionality, content, and services
                accessible through the Site (collectively, the “<strong>Services</strong>”) are
                offered by GlassHive, LLC, a Texas limited liability company
                (“<strong>GlassHive</strong>,” “<strong>we</strong>,” or “<strong>us</strong>”).
                This policy (“Privacy Policy”) describes the privacy practices of GlassHive and how
                we may use your personally identifiable information and unidentifiable information
                that you provide to us through use of the Services. All other information given to
                us is outside the scope of this policy. This Privacy Policy will explain what
                information is collected, how that information is used and/or shared, how the
                information is protected, and how you can control your privacy while using the
                service. We strive to make all choices you make while using the Services as
                transparent as possible. We also hold the security of your personal data as our top
                priority.
            </p>
            <p>
                Please read carefully to understand our policies and practices regarding your
                information and how we will treat it. If you do not agree with our policies and
                practices, do not register with the Services. By registering with or using the
                Services, you agree to this Privacy Policy. This policy may change from time to
                time. Your continued use of the Services after we make changes is deemed to be
                acceptance of those changes, so please check the policy periodically for updates.
            </p>

            <h3>Data Definitions</h3>
            <p class="indented">
                <strong>Personal Data</strong> means information about you that is personally
                identifiable to you, such as your contact information (e.g., name, email address,
                profile photo, birthdate, phone number, username, password, billing information,
                business name, job title, and other registration information), and any other
                non-public information that is associated with such information.
            </p>
            <p class="indented">
                <strong>Non-Personal Data</strong> means information about you but that does not
                identify you, including usage information, internet connection, operating systems,
                and browser type.
            </p>

            <h3>Data Categories</h3>
            <p class="indented">
                <strong>Customer Data</strong> is data that we collect, process, and manage on
                behalf of our customers. This data is processed by us under the instruction of the
                customer as our role of a "data processor".
            </p>
            <p class="indented">
                <strong>User Data</strong> is data related to our customers' people who engage with
                the GlassHive platform as users.
            </p>
            <p class="indented">
                <strong>Prospect Data</strong> is data related to visitors of our websites,
                participants at events, or person who interacts with one of our forms of digital
                communication (social media, digital ads, email, etc).
            </p>

            <h3>Data Controller/Processor</h3>
            <p>
                GlassHive is the data processor of Customer Data, subject to our Terms of Service
                and other agreements with said Customer.
            </p>
            <p>GlassHive is the data controller of User Data and Prospect Data.</p>

            <h3>How We Collect Data</h3>
            <p>
                During your use of the Services, GlassHive may collect Personal Data and
                Non-Personal Data.
            </p>
            <p>
                <strong>Information Provided by You:</strong> You may affirmatively give us
                information about you by registering with the Services, filling in forms on the
                Services, integrating your GlassHive account with third party services, or by
                corresponding with us through the forums, chat, e-mail, website, phone, or
                otherwise. This includes information you provide when you register to use our
                Services, information provide to set up payments, and information given you report a
                problem with our Services. The information you give us may include Personal
                Information.
            </p>
            <p>
                <strong>Information Automatically Collected by GlassHive:</strong>
                Like other websites or applications, we also may automatically collect and store
                information that is generated as you navigate through the Services. We may collect
                Personal and Non-Personal Data about your visit from the browser on your computer or
                mobile device including but not limited to the time and date you access the Services
                and the pages you request. We use this information to administer the Services,
                improve our understanding of customer needs, analyze trends, track navigation of the
                site, personalize the site for you, and gather broad demographic information for
                aggregate use. To automatically collect information, we use various technologies
                including:
            </p>
            <ol class="indented">
                <li>
                    <em>Analytics</em>: We use various analytics services, including Google
                    Analytics and Intercom, to better understand how visitors interact with our
                    Services. These analytics services provide non-personally identifiable data
                    including but not limited to data on where visitors came from, what actions they
                    took on our site and where visitors went when they left our site.
                </li>
                <li>
                    <em>Cookies (browser cookies)</em>: A cookie is a small file placed on the hard
                    drive of your computer. You may refuse to accept browser cookies by activating
                    the appropriate setting on your browser. If you select this setting, however,
                    you may be unable to access certain parts of our Site. Unless you have adjusted
                    your browser setting so that it will refuse cookies, our system will issue
                    cookies when you direct your browser to our Site.
                </li>
                <li>
                    <em>Web Beacons</em>: Pages of our Site and e-mails may contain small electronic
                    files known as web beacons (also referred to as clear gifs, pixel tags, and
                    single-pixel gifs) that permit us, for example, to count users who have visited
                    those pages or opened an email and for other related website statistics (for
                    example, recording the popularity of certain Site content and verifying system
                    and server integrity).
                </li>
                <li>
                    <em>Log Files</em>. Like most standard websites, we use log files. This
                    information may include internet protocol (IP) addresses, browser type, internet
                    service provider (ISP), referring/exit pages, platform type, date/time stamp,
                    and number of clicks to monitor error messages, analyze trends, administer the
                    site, track user's movement in the aggregate, and gather broad demographic
                    information for aggregate use. However, none of the information stored in our
                    log files, including but not limited to IP addresses, is linked to personally
                    identifiable information.
                </li>
            </ol>
            <p>
                <strong>Information from integrations:</strong> You or one of the admins of your
                account may choose to turn on a third-party integration. The third-party software
                may send/receive information to/from GlassHive.
            </p>
            <p>
                <strong>Information from other third parties:</strong> We may obtain Personal
                Information from other third parties, such as events, webinars, or promotions that
                we sponsor or are involved with. This data may also be obtained from service
                providers, partners, or business networking platforms like LinkedIn.
            </p>

            <h3>How We Use Data</h3>
            <p>
                We will use the information you provide to us for the performance of a contract, for
                legal obligations, and for legitimate interests. These uses include:
            </p>
            <ol>
                <li>
                    To create a public profile for your account, including your name, photo, and
                    username (Performance of Contract, Legitimate Interests);
                </li>
                <li>
                    To create your GlassHive account (Performance of Contract, Legitimate
                    Interests);
                </li>
                <li>
                    To provide you with the Services (Performance of Contract, Legitimate
                    Interests);
                </li>
                <li>
                    To carry out our obligations and enforce our rights arising from any contracts
                    entered into between you and us, including for billing and collection
                    (Performance of Contract, Legitimate Interests);
                </li>
                <li>
                    To contact you, via phone or email, for support and feedback requests
                    (Performance of Contract, Legitimate Interests);
                </li>
                <li>
                    To contact you, via phone or email, to inform you of important changes or
                    additions to our Services (Performance of Contract, Legitimate Interests);
                </li>
                <li>To administer contests, promotions, or surveys (Legitimate Interests);</li>
                <li>
                    To send you administrative notices, offers and any other communications that we
                    believe may be of interest to you (Performance of Contract, Legitimate
                    Interests, Consent);
                </li>
                <li>
                    To operate, improve and maintain our products and services (Performance of
                    Contract, Legitimate Interests);
                </li>
                <li>To track and monitor web traffic (Legitimate Interests);</li>
                <li>To implement SPAM protection (Legitimate Interests);</li>
                <li>
                    Any other purpose for which you give us authorization (Performance of Contract,
                    Legitimate Interests)
                </li>
            </ol>
            <p>
                The usage information we collect helps us to improve our Services and to deliver a
                better and more personalized experience by enabling us to:
            </p>
            <ol>
                <li>Estimate our audience size and usage patterns;</li>
                <li>Speed up responses; and</li>
                <li>Recognize you when you use the Site.</li>
            </ol>
            <p>
                We may also use your information to contact you about our own and third-parties'
                goods and services that may be of interest to you. If you do not want us to use your
                information in this way, please adjust your user preferences in your account profile
                or contact us.
            </p>
            <p>
                If you reside or are using the Services in a territory governed by privacy laws
                under which consent is the only or most appropriate legal basis for processing
                personal data as described in this Privacy Policy, your acceptance of our Terms and
                of this Privacy Policy will be deemed as your consent to the processing of your
                personal data for all purposes detailed in this Privacy Policy, unless applicable
                law requires a different form of consent. If you wish to revoke such consent, please
                contact us at <a href="mailto:support@glasshive.com">support@glasshive.com</a>.
            </p>
            <p><strong>Limits on Use of Your Google User Data</strong></p>
            <p>
                Notwithstanding anything else in this Privacy Policy, if you provide the App access
                to the following types of your Google data, the App's use of that data will be
                subject to these additional restrictions:
            </p>
            <p class="indented">
                The App will only use access to read, write, modify, or control Gmail message bodies
                (including attachments), metadata, headers, and settings to provide a web email
                client that allows users to compose, send, read, and process emails and will not
                transfer this Gmail data to others unless doing so is necessary to provide and
                improve these features, comply with applicable law, or as part of a merger,
                acquisition, or sale of assets.
            </p>
            <p class="indented">The App will not use this Gmail data for serving advertisements.</p>
            <p class="indented">
                The App will not allow humans to read this data unless we have your affirmative
                agreement for specific messages, doing so is necessary for security purposes such as
                investigating abuse, to comply with applicable law, or for the App's internal
                operations and even then only when the data have been aggregated and anonymized.
            </p>
            <p class="indented">
                GlassHive's use and transfer to any other app of information received from Google
                APIs will adhere to
                <a
                    target="_blank"
                    href="https://developers.google.com/terms/api-services-user-data-policy"
                    >Google API Services User Data Policy</a
                >, including the Limited Use requirements.
            </p>

            <h3>How We Share Data</h3>
            <p>
                When you send information to the Services, you are granting us permission to
                disclose and take other actions regarding such information, to provide the Services.
                We do not share, sell, rent or otherwise distribute this information to others,
                except as disclosed in this Privacy Policy. We may disclose Personal Data that we
                collect or you provide, as permitted or allowed by law, as follows:
            </p>
            <ol>
                <li>
                    <em>Internally</em>: We may share information with our subsidiaries and
                    affiliates.
                </li>
                <li>
                    <em>With Your Consent</em>: We may share information when you provide us with
                    your consent to do so. This may occur when you consent aﬃrmatively or it may
                    occur through your use of the Services as provided in this Privacy Policy.
                </li>
                <li>
                    <em>Service Providers and Contractors</em>: We reserve the right to disclose to
                    service providers, contractors, or other third parties any or all of the
                    information that we collect through the Services about you to conduct business
                    on our behalf. These activities could include processing or making payments,
                    maintaining the Services, or facilitating email delivery to you. We require that
                    our service providers and contractors protect your Personal Data with the same
                    standards we do but we are not responsible for their policies or practices.
                </li>
                <li>
                    <em>By Law or to Protect Rights</em>: We may disclose inormation when we believe
                    in good faith that disclosure is necessary to protect your safety or the safety
                    of others, to comply with the law, to enforce our intellectual property rights,
                    to enforce our
                    <a href="/Account/TermsOfService" target="_blank">Terms of Service</a> , or to
                    protect the rights, property or safety of GlassHive and our employees, and if
                    necessary to defend against third-party claims. We may also disclose information
                    when requested to comply with a court order, investigation, or governmental
                    request.
                </li>
                <li>
                    <em>Business Transfers</em>: If we go through a business transfer, such as
                    consolidation, merger, restructuring, acquisition, or sale of part or all of our
                    assets, you acknowledge and consent to the transfer of your information. You
                    further acknowledge and consent to the continued use of your information by the
                    recipient so long as they comply with this Privacy Policy.
                </li>
            </ol>
            <p>
                Please bear in mind that whenever you voluntarily make your Personal Data available
                for viewing by third parties online - for example through comments and participation
                in forums - that information can be seen, collected and used by others besides us.
                We cannot be responsible for any unauthorized third-party use of such information.
            </p>
            <p>
                <strong>Use and Disclosure of Non-Personal Data</strong>
            </p>
            <p>
                GlassHive may share with third parties your Non-Personal Data and related aggregated
                data created by us without restriction, including with marketing partners. Because
                this form of data does not identify particular users, third parties will not be able
                to identify you based solely on this data. For example, we may share aggregated
                usage data or may share general demographic information to provide anonymous
                aggregated data to business partners or other organizations about the volume of use
                on the Services.
            </p>

            <h3 id="tracking-technology">Tracking Technology</h3>
            <p>
                Our Sites and Service, along with some of our Service Providers, use technologies
                such as "cookies", anonymous identifiers, container tags, and others to deliver and
                optimize our Service, analyze performance and marketing efforts, and customize your
                experience. These technologies may be temporarily stored on your device. Some
                cookies and similar technologies are used to retrieve Personal Data, like an IP
                address, that you have previously provided.
            </p>
            <p>
                You can set your browser or mobile device to refuse all or some browser cookies, or
                to alert you when cookies are being sent. If you disable or refuse cookies, please
                note that some parts of the Site may then be inaccessible or not function properly.
            </p>
            <p>
                Some web browsers may transmit "do-not-track" signals to websites with which the
                browser communicates. Our Site does not currently respond to these “do-not-track”
                signals.
            </p>
            <p>
                If you do not want us to collect this information do not create an account and do
                not use the Site. You may also choose to disable certain aspects of the Services
                within your user account. To learn more about our practices concerning cookies and
                tracking, please see our
                <a href="/Account/CookiePolicy" target="_blank">Cookie Policy</a>.
            </p>

            <h3>Communication</h3>
            <p>
                <strong>Service:</strong> We may reach out to you with important information about
                our Service. This could include notifications about changes to our Service, billing,
                operations occuring in your account, etc. You can manage your notification
                preferences through your User settings. However, please be aware that you cannot opt
                out of certain essential service communications, such as those related to password
                resets or billing notifications.
            </p>
            <p>
                <strong>Promotional:</strong> If you do not wish to have your contact information
                used by the GlassHive to promote our own or third parties' products or services, you
                can opt-out by sending us an email stating your request to
                <a href="mailto:support@glasshive.com">support@glasshive.com</a>. If we have sent
                you a promotional email, you may either unsubscribe within the message or send us a
                return email asking to be omitted from future email distributions.
            </p>

            <h3>Retention</h3>
            <p>
                We may retain certain information as required by law or for necessary business
                purposes. We are under no obligation to store such Personal Data indefinitely and
                disclaim any liability arising out of, or related to, the destruction of such
                Personal Data. In addition, you should be aware that it is not always possible to
                completely remove or delete all of your information from our databases without some
                residual data because of backups and other reasons. For example, once we disclose
                your information to third parties, we may not be able to access that information any
                longer and cannot force the deletion or modification of any such information by the
                parties to whom we have made those disclosures. We are under no obligation to
                destroy anonymized aggregate data.
            </p>

            <h3>Data Security</h3>
            <p>
                We use industry standard physical, technical, and internal administrative security
                measures to protect the confidentiality and security of your information, including
                encryption where appropriate. However, as effective as the reasonable security
                measures we implement may be, no security system is impenetrable. As a result, we
                cannot guarantee, ensure, or warrant the security of any information submitted to
                us. It is your responsibility to protect the security of your login information and
                to use good judgment before deciding to send information via the Internet. Please
                note that e-mails and other communications you send to us through the Services are
                not encrypted, and we strongly advise you not to communicate any confidential
                information through these means.
            </p>
            <p>
                By using the Services or providing Personal Data to us, you agree that we may
                communicate with you electronically regarding security, privacy, and administrative
                issues relating to your use of the Services. If we learn of a security system's
                breach, we may attempt to notify you electronically by posting a notice on the
                Services, by phone call, or by sending an email to you. You may have a legal right
                to receive this notice in writing. To receive free written notice of a security
                breach, please contact us at the contact information listed below.
            </p>

            <h3>Data Location and Transfers</h3>
            <p>
                Our computer systems for the Services are currently governed by and operated in the
                United States. GlassHive is committed to protect personal data in accordance with
                this Privacy Policy and appropriate laws, regardless of lesser legal obligations
                that may apply to our location of processing.
            </p>
            <p>
                GlassHive complies with the EU-U.S. Data Privacy Framework, UK Extension to the
                EU-US Data Privacy Framework, and the Swiss-U.S. Data Privacy Framework
                (collectively, the “Data Privacy Framework”) as set forth by the U.S. Department of
                Commerce regarding the collection, use, and retention of personal data transferred
                from the European Union, UK, and Switzerland, as applicable, to the United States in
                reliance on the Data Privacy Framework. GlassHive has certified to the Department of
                Commerce that it adheres to the Data Privacy Framework Principles with respect to
                such data. If there is any conflict between the terms in this notice and the Data
                Privacy Framework Principles, the Data Privacy Framework Principles shall govern. To
                learn more about the Data Privacy Framework program, and to view our certification,
                please visit
                <a href="https://www.dataprivacyframework.gov" target="_blank">
                    https://www.dataprivacyframework.gov</a
                >.
            </p>

            <h3>Additional Notices</h3>
            <p><strong>Data Deletion Requests</strong></p>
            <p>
                If you wish to request the deletion of your personal data, please contact us at
                <a href="mailto:support@glasshive.com">support@glasshive.com</a> with the subject
                line "Data Deletion Request". Please include your name, email address, and a
                detailed description of the data you wish to have deleted. We will respond to your
                request within one calendar month of receipt. If your request is complex, this
                timeline may be extended up to three calendar months from the day of receipt.
            </p>
            <p><strong>Data Subject Rights</strong></p>
            <p>
                If you wish to exercise any of your data subject rights, including but not limited
                to access, rectification, deletion, or data portability, please contact us at
                <a href="mailto:support@glasshive.com">support@glasshive.com</a> with the subject
                line "Data Subject Rights Request". Please include your name, email address, and a
                detailed description of your request. We will respond to your request within one
                calendar month of receipt. If your request is complex, this timeline may be extended
                up to three calendar months from the day of receipt. If you are a GDPR-protected
                individual, you also have the right to lodge a complaint with the relevant
                supervisory authority.
            </p>
            <p><strong>Third Party Links</strong></p>
            <p>
                This privacy statement applies solely to information collected by our Services. The
                Services may contain links to or provide integrations with other sites or services.
                We are not responsible for the privacy policies or other practices employed by
                websites linked to or from, or integrated with, our website nor the information or
                content contained therein, and we encourage you to read the privacy statements of
                any linked or integrated third party.
            </p>
            <p><strong>Children Under the Age of 13</strong></p>
            <p>
                Our Services are not intended for children under 13 years of age. No one under age
                13 may provide any Personal Data to or on the Site. We do not knowingly collect
                Personal Data from children under 13. If you are under 13, do not use or register
                with the Services, make any purchases through the Site, use any of the interactive
                or public comment features of this Site or provide any information about yourself to
                us. If we learn we have collected or received Personal Data from a child under 13
                without verification of parental consent, we will delete that information. If you
                believe we might have any information from or about a child under 13, please contact
                us at
                <a href="mailto:support@glasshive.com">support@glasshive.com</a>.
            </p>
            <p><strong>CALOPPA Disclosure</strong></p>
            <p>
                California Civil Code Section 1798.83 permits users of our Services that are
                California residents to request certain information regarding our disclosure of
                Personal Data to third parties for their direct marketing purposes. To make such a
                request, please send an email to
                <a href="mailto:support@glasshive.com">support@glasshive.com</a>.
            </p>

            <p><strong>Changes and Updates</strong></p>
            <p>
                If we make material changes to how we treat our users' Personal Data, we will notify
                you by email to the email address specified in your account and/or through a notice
                on the Site home page. The date the Privacy Policy was last revised is identified at
                the top of the page. You are responsible for ensuring we have an up-to-date active
                and deliverable email address for you, and for periodically visiting our Site and
                this Privacy Policy to check for any changes.
            </p>

            <p><strong>Inquiries and complaints</strong></p>
            <p>
                If you believe GlassHive maintains your personal data in one of the services within
                the scope of our Data Privacy Framework certification, you may direct any inquiries
                or complaints concerning our Data Privacy Framework compliance to
                <a href="mailto:support@glasshive.com">support@glasshive.com</a>. GlassHive will
                respond within one calendar month of the day of receipt. If your request is complex,
                this timeline may be extended up to three calendar months from the day of receipt.
            </p>

            <p><strong>Third parties who may receive personal data</strong></p>
            <p>
                GlassHive uses a limited number of third-party service providers to assist us in
                providing our services to customers. These third party providers offer customer
                support to our customers, perform database monitoring and other technical
                operations, assist with the transmission of data, and provide data storage services.
                These third parties may access, process, or store personal data in the course of
                providing their services. GlassHive maintains contracts with these third parties
                restricting their access, use and disclosure of personal data in compliance with our
                Data Privacy Framework obligations, including the onward transfer provisions, and
                GlassHive remains liable if they fail to meet those obligations and we are
                responsible for the event giving rise to damage.
            </p>

            <p><strong>Dispute resolution</strong></p>
            <p>
                If a privacy complaint or dispute relating to Personal Data received by GlassHive
                LLC in reliance on the Data Privacy Framework (or any of its predecessors) cannot be
                resolved through our internal processes, we have agreed to participate in the
                <a
                    href="https://verasafe.com/public-resources/dispute-resolution/dispute-resolution-procedure/"
                    target="_blank"
                >
                    VeraSafe Data Privacy Framework Dispute Resolution Procedure </a
                >. Subject to the terms of the VeraSafe Data Privacy Framework Dispute Resolution
                Procedure, VeraSafe will provide appropriate recourse free of charge to you. To file
                a complaint with VeraSafe and participate in the VeraSafe Data Privacy Framework
                Dispute Resolution Procedure, please submit the required information here:
                <a
                    href="https://www.verasafe.com/privacy-services/dispute-resolution/submit-dispute/"
                    target="_blank"
                >
                    https://www.verasafe.com/privacy-services/dispute-resolution/submit-dispute/</a
                >.
            </p>

            <p><strong>Binding Arbitration</strong></p>
            <p>
                If your dispute or complaint related to your Personal Data that we received in
                reliance on the Data Privacy Framework cannot be resolved by us, nor through the
                dispute resolution mechanism mentioned above, you may have the right to require that
                we enter into binding arbitration with you under the Data Privacy Framework
                “Recourse, Enforcement and Liability” Principle and Annex I of the Data Privacy
                Framework.
            </p>

            <p><strong>Your rights to access, to limit use, and to limit disclosure</strong></p>
            <p>
                EU, UK, and Swiss individuals have rights to access personal data about them, and to
                limit use and disclosure of their personal data. With our Data Privacy Framework
                self-certification, GlassHive has committed to respect those rights. If you wish to
                request access, to limit use, or to limit disclosure, please provide the name of the
                GlassHive customer who submitted your data to our services. We will refer your
                request to that customer, and will support them as needed in responding to your
                request.
            </p>

            <p><strong>U.S. Federal Trade Commission enforcement</strong></p>
            <p>
                GlassHive's commitments under the Data Privacy Framework are subject to the
                investigatory and enforcement powers of the United States Federal Trade Commission.
            </p>

            <p><strong>Compelled disclosure</strong></p>
            <p>
                GlassHive may be required to disclose personal data in response to lawful requests
                by public authorities, including to meet national security or law enforcement
                requirements.
            </p>

            <p><strong>Data Protection Officer</strong></p>
            <p>
                GlassHive has appointed a data protection officer, Bill Wettingfeld. The appointed
                person may be reached at
                <a href="mailto:privacy@glasshive.com">privacy@glasshive.com</a>.
            </p>

            <p><strong>EU GDPR Representative</strong></p>
            <p>
                Rickert Rechtsanwaltsgesellschaft mbH<br />
                - GlassHive LLC -<br />
                Colmantstraße 15<br />
                53115 Bonn<br />
                Germany<br />
                <a href="mailto:art-27-rep-glasshive@rickert.law">
                    art-27-rep-glasshive@rickert.law
                </a>
            </p>

            <p><strong>UK GDPR Representative</strong></p>
            <p>
                Rickert Services Ltd UK<br />
                - GlassHive LLC -<br />
                PO Box 1487<br />
                Peterborough<br />
                PE1 9XX<br />
                United Kingdom<br />
                <a href="mailto:art-27-rep-glasshive@rickert-services.uk">
                    art-27-rep-glasshive@rickert-services.uk
                </a>
            </p>

            <p><strong>Swiss GDPR Representative</strong></p>
            <p>
                HÄRTING Attorneys-at-Law Ltd.<br />
                Landis + Gyr-Strasse 1<br />
                6300 Zug<br />
                <a href="mailto:Swissdatarep@haerting.ch">Swissdatarep@haerting.ch</a>
            </p>

            <p><strong>Microsoft Privacy Policy</strong></p>
            <p>
                If you are leveraging GlassHive as a Microsoft employee service or your data
                pertains to Microsoft Personal Data, then
                <a href="https://www.microsoft.com/en-us/privacy/privacystatement" target="_blank">
                    Microsoft's Privacy Policy
                </a>
                applies instead.
            </p>

            <p><strong>Contact Us</strong></p>
            <p>
                We welcome your feedback regarding this Privacy Policy. If you have questions,
                comments, or concerns about either one, please contact us by e-mail at
                <a href="mailto:support@glasshive.com">support@glasshive.com</a>.
            </p>

            <p>End.</p>
        </div>
    </div>
</template>

<script>
export default {
    data: function () {
        return {
            isInitializing: false,
        };
    },

    created() {},
};
</script>

<style>
.disclaimer .container {
    margin: 0 auto;
    max-width: 960px;
    overflow: initial;
    padding: 20px 24px;
}

.disclaimer sup {
    position: relative;
    font-size: 40%;
    line-height: 0;
    vertical-align: baseline;
    top: -1.2em;
}

.disclaimer .title {
    text-align: center;
}

.disclaimer .title,
.disclaimer .pertinent {
    text-transform: uppercase;
}

.disclaimer ol {
    counter-reset: item;
}

.disclaimer li {
    display: block;
    margin-bottom: 15px;
}

.disclaimer li::before {
    content: counters(item, '.') ' ';
    counter-increment: item;
}

.disclaimer a {
    color: #0645ad;
}

.disclaimer .underlined {
    text-decoration: underline;
}

.disclaimer.page .alpha-list {
    list-style-type: lower-alpha;
}

.disclaimer.page .alpha-list li {
    display: list-item;
}

.disclaimer.page .alpha-list li::before {
    content: '';
}

.disclaimer .indented {
    margin-left: 30px;
}

.disclaimer.page h3 {
    margin-top: 50px;
}
</style>
